import React, {useCallback, useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {Link, useNavigate, useParams} from 'react-router-dom';
import I18n from 'app/i18n';
import {betMoney, getLinkPath} from 'app/utils';
import {configStore, userStore} from 'app/store/global';
import {widgetShellStyles} from 'app/components/pages/poker/ProomPokerWidgetShell';
import PokerTableFrame from 'app/components/pages/poker/PokerTableFrame';
import type {ProomTableInfo} from 'app/utils/proom/proomMessages';
import {
    claimBoardId,
    releaseBoardId,
    requestRestoreTable
} from 'app/utils/proom/proomTableCoordination';

const TABLE_BG = '#0f0f0f';

const stubStyles: Record<string, React.CSSProperties> = {
    container: {
        alignItems: 'center',
        background: TABLE_BG,
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        inset: 0,
        justifyContent: 'center',
        padding: 24,
        position: 'fixed',
        textAlign: 'center'
    },
    link: {
        background: '#fe9400',
        borderRadius: 3,
        color: '#000',
        fontSize: 13,
        fontWeight: 700,
        padding: '10px 20px',
        textDecoration: 'none',
        textTransform: 'uppercase'
    },
    text: {color: '#c5c5c5', fontSize: 16, margin: 0}
};

const ProomPokerTableUnavailableStub = (): React.ReactElement => <div style={stubStyles.container}>
    <p style={stubStyles.text}>{I18n.t('poker.proom_unavailable')}</p>
    <Link style={stubStyles.link} to={getLinkPath('/poker')}>
        {I18n.t('poker.title')}
    </Link>
</div>;

const ProomPokerTableLeftStub = (): React.ReactElement => <div style={stubStyles.container}>
    <p style={stubStyles.text}>Вы покинули стол</p>
    <Link style={stubStyles.link} to={getLinkPath('/poker')}>
        Вернуться в лобби
    </Link>
</div>;

// eslint-disable-next-line max-statements
const ProomPokerTablePage = observer((): React.ReactElement => {
    const {boardId} = useParams<{boardId: string}>();
    const navigate = useNavigate();
    const {user} = userStore;
    const externalId = String(user?.id || '');
    const [leftTable, setLeftTable] = useState(false);
    const [tableInfo, setTableInfo] = useState<ProomTableInfo>({
        blinds: '',
        currency: '',
        name: ''
    });
    const [latestUserToken, setLatestUserToken] = useState(
        configStore.PROOM_CONFIG.userToken || (window as Window & {PROOM_CONFIG?: {userToken?: string}}).PROOM_CONFIG?.userToken || ''
    );

    const balanceLabel = betMoney({amount: user?.balance || user?.full_balance || '0'});
    const playerProfilesBaseUrl = configStore.PROOM_CONFIG.playerProfilesBaseUrl || window.location.origin;
    const playerProfilesToken = configStore.PROOM_CONFIG.playerProfilesToken || '';
    const parentOrigin = configStore.PROOM_CONFIG.parentOrigin || '';
    const tableBlindsLabel = [tableInfo.blinds, tableInfo.currency].filter(Boolean).join(' · ');

    useEffect(() => {
        if (boardId) {
            claimBoardId(boardId);
        }

        return () => {
            if (boardId) {
                releaseBoardId(boardId);
            }
        };
    }, [boardId]);

    const handleLeave = useCallback(() => {
        if (boardId) {
            releaseBoardId(boardId);
        }

        window.close();

        window.setTimeout(() => {
            setLeftTable(true);
        }, 100);
    }, [boardId]);

    const handleRestore = useCallback(() => {
        if (!boardId) {
            return;
        }

        requestRestoreTable(boardId, externalId);
        releaseBoardId(boardId);
        window.close();

        window.setTimeout(() => {
            setLeftTable(true);
        }, 100);
    }, [boardId, externalId]);

    const handleTableTransfer = useCallback((newBoardId: string) => {
        navigate(getLinkPath(`/poker/table/${newBoardId}`), {replace: true});
    }, [navigate]);

    const handleInfo = useCallback((info: ProomTableInfo) => {
        setTableInfo(info);
    }, []);

    const handleUserTokenRefreshed = useCallback((userToken: string) => {
        setLatestUserToken(userToken);
    }, []);

    if (!externalId || !boardId) {
        return <ProomPokerTableUnavailableStub/>;
    }

    if (leftTable) {
        return <ProomPokerTableLeftStub/>;
    }

    return <>
        <style>{widgetShellStyles}</style>
        <PokerTableFrame
            callbacks={{
                onClose: handleLeave,
                onInfo: handleInfo,
                onLeave: handleLeave,
                onMinimize: handleRestore,
                onTableTransfer: handleTableTransfer,
                onUserTokenRefreshed: handleUserTokenRefreshed
            }}
            mode="standalone"
            session={{
                balanceLabel,
                nickname: user?.name || '',
                parentOrigin,
                playerProfilesBaseUrl,
                playerProfilesToken,
                userToken: latestUserToken
            }}
            table={{
                blinds: tableBlindsLabel,
                boardId,
                externalId,
                name: tableInfo.name
            }}
        />
    </>;
});

export default ProomPokerTablePage;
